import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useRef, useMemo, useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { FiChevronDown } from 'react-icons/fi';

import { Spinner, Scrollbar, IconButton, Fade } from '@mssgme/ui';

import { processMessages } from './stream';
import useMessageList from './useMessageList';
import StartConversation from './StartConversation';
import MessageGroup from './MessageGroup';
import DateBreak from './DateBreak';
import SystemMessage from './SystemMessage';

import styles from './MessageList.scss';

PureMessageList.propTypes = {
    items: PropTypes.array.isRequired,
    hasMore: PropTypes.bool,
    conversation: PropTypes.object.isRequired,
    onScroll: PropTypes.func,
};

PureMessageList.defaultProps = {
    hasMore: false,
    onScroll: () => {},
};

export function PureMessageList({ items, hasMore, onScroll, conversation }) {
    const rootRef = useRef();
    const prevScrollHeightRef = useRef(0);
    const [isStick, setIsStick] = useState(true);

    const groupedMessages = useMemo(() => Array.from(processMessages(items, hasMore)), [items, hasMore]);

    const scrollTo = (to = 0) => {
        rootRef.current.scrollTop = rootRef.current.scrollHeight + to;
    };

    const scrollBottom = useCallback(() => scrollTo(0), []);

    useEffect(() => {
        if (isStick) {
            scrollBottom();
        } else {
            scrollTo(-prevScrollHeightRef.current);
        }
    }, [items.length]);

    useEffect(() => {
        prevScrollHeightRef.current = rootRef.current.scrollHeight;
    });

    const handleScroll = useCallback(
        (e) => {
            setIsStick(rootRef.current.scrollHeight - rootRef.current.scrollTop === rootRef.current.clientHeight);
            onScroll(e);
        },
        [onScroll]
    );

    const renderItem = ({ type, messages, base }) => {
        switch (type) {
            case 'group': {
                const author =
                    base.destination === 'organization'
                        ? { name: conversation.contact.name, avatarUrl: conversation.contact.avatarUrl }
                        : { name: messages[0].name };

                return (
                    <MessageGroup
                        key={`group-${messages[0]._id}`}
                        destination={base.destination}
                        messages={messages}
                        author={author}
                    />
                );
            }
            case 'date':
                return <DateBreak key={`${base._id}-date-break-${base.received}`} time={moment(base.received)} />;
            case 'system':
                return messages.map((m) => (
                    <SystemMessage key={m._id} type={m.type} time={moment(m.received)} payload={m.payload} />
                ));
            default:
                throw new Error('Invalid item type');
        }
    };

    return (
        <div className={styles.root}>
            <Scrollbar>
                <div className={styles.list} ref={rootRef} onScroll={handleScroll}>
                    {hasMore ? (
                        <Spinner size={26} />
                    ) : (
                        <StartConversation
                            platform={conversation.contact.client.platform}
                            time={moment(conversation.started)}
                        />
                    )}

                    {groupedMessages.map(renderItem)}
                </div>
            </Scrollbar>
            <Fade in={!isStick}>
                <div className={styles.downBtnWrap}>
                    <IconButton className={styles.downBtn} onClick={scrollBottom}>
                        <FiChevronDown />
                    </IconButton>
                </div>
            </Fade>
        </div>
    );
}

export default function MessageList(props) {
    const { contactId } = useParams();
    const state = useMessageList(contactId);

    return <PureMessageList {...state} {...props} />;
}
